
import { useState, useRef, useCallback } from "react";
import { CellPosition, ColumnKey, Week } from "../types/workout-types";
import { toast } from "sonner";

export function useWorkoutEditing(
  weeks: Week[],
  currentWeekIndex: number, 
  currentDayIndex: number, 
  activeCell: CellPosition | null,
  editMode: boolean,
  setActiveCell: (cell: CellPosition | null) => void,
  setEditMode: (edit: boolean) => void,
  setTempValue: (value: string) => void,
  tempValue: string,
  saveToHistory: () => void,
  setWeeks: (weeks: Week[]) => void
) {
  const [hasEdited, setHasEdited] = useState(false);
  const originalValueRef = useRef<string>("");
  const inputRefs = useRef<Record<string, HTMLInputElement | null>>({});
  const lastActionTimeRef = useRef<number>(Date.now());
  
  // Auto-save timer reference
  const autoSaveTimerRef = useRef<number | null>(null);

  const handleCellChange = useCallback((id: string, field: ColumnKey, value: string) => {
    const currentDay = weeks[currentWeekIndex]?.days[currentDayIndex];
    const exercise = currentDay?.exercises.find((e) => e.id === id);
    
    if (exercise && exercise[field] !== value) {
      saveToHistory();

      const updatedWeeks = JSON.parse(JSON.stringify(weeks));
      const updatedExercises = updatedWeeks[currentWeekIndex].days[currentDayIndex].exercises.map((ex) =>
        ex.id === id ? { ...ex, [field]: value } : ex
      );
      updatedWeeks[currentWeekIndex].days[currentDayIndex].exercises = updatedExercises;
      setWeeks(updatedWeeks);
      
      // Show success toast
      toast.success(`${field.charAt(0).toUpperCase() + field.slice(1)} updated`, {
        position: "bottom-right",
        duration: 1500,
      });
    }
  }, [weeks, currentWeekIndex, currentDayIndex, saveToHistory, setWeeks]);

  const handleNotesChange = useCallback((notes: string) => {
    saveToHistory();

    const updatedWeeks = JSON.parse(JSON.stringify(weeks));
    updatedWeeks[currentWeekIndex].days[currentDayIndex].notes = notes;
    setWeeks(updatedWeeks);
  }, [weeks, currentWeekIndex, currentDayIndex, saveToHistory, setWeeks]);

  const handleCellClick = useCallback((rowIndex: number, colIndex: number, value: string) => {
    // Prevent rapid successive clicks (debounce)
    const now = Date.now();
    if (now - lastActionTimeRef.current < 200) {
      return;
    }
    
    lastActionTimeRef.current = now;
    originalValueRef.current = value;
    setHasEdited(false);

    // Set active cell without going into edit mode
    setActiveCell({ rowIndex, colIndex });
    setEditMode(false);
    setTempValue(value);
  }, [setActiveCell, setEditMode, setTempValue]);

  const startEditing = useCallback((rowIndex: number, colIndex: number, value: string) => {
    // Prevent rapid successive edits (debounce)
    const now = Date.now();
    if (now - lastActionTimeRef.current < 300) {
      return;
    }
    
    lastActionTimeRef.current = now;
    originalValueRef.current = value;
    setHasEdited(false);

    // First set the temp value to avoid flickering
    setTempValue(value);
    
    // Then set active cell and edit mode
    setActiveCell({ rowIndex, colIndex });
    setEditMode(true);
    
    // Clear any existing auto-save timer
    if (autoSaveTimerRef.current !== null) {
      window.clearTimeout(autoSaveTimerRef.current);
      autoSaveTimerRef.current = null;
    }
  }, [setActiveCell, setEditMode, setTempValue]);

  const finishEditingWithSave = useCallback((id: string, field: ColumnKey, value: string) => {
    if (hasEdited && originalValueRef.current !== value) {
      handleCellChange(id, field, value);
    }
    
    // Small delay before clearing edit mode helps prevent flickering
    setTimeout(() => {
      setEditMode(false);
      setHasEdited(false);
    }, 50);
    
    // Clear any existing auto-save timer
    if (autoSaveTimerRef.current !== null) {
      window.clearTimeout(autoSaveTimerRef.current);
      autoSaveTimerRef.current = null;
    }
  }, [hasEdited, setEditMode, handleCellChange]);

  const cancelEditing = useCallback(() => {
    // Small delay before clearing edit mode helps prevent flickering
    setTimeout(() => {
      setEditMode(false);
      setHasEdited(false);
    }, 50);
    
    // Clear any existing auto-save timer
    if (autoSaveTimerRef.current !== null) {
      window.clearTimeout(autoSaveTimerRef.current);
      autoSaveTimerRef.current = null;
    }
  }, [setEditMode]);
  
  return {
    hasEdited,
    setHasEdited,
    originalValueRef,
    inputRefs,
    handleCellChange,
    handleNotesChange,
    handleCellClick,
    startEditing,
    finishEditingWithSave,
    cancelEditing
  };
}

// Define columns for keyboard navigation (used in the effect)
const columns: ColumnKey[] = ["exercise", "sets", "reps", "kgs", "rpe", "rest", "notes"];
